import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Row, SectionHeader, Table, useLoad } from './kit';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
type Schedule = Partial<Record<DayKey, [string, string][]>>;
interface HoursRow {
  id: string;
  name: string;
  timezone: string;
  schedule: Schedule;
  holidays: Holiday[];
  queues: string[];
}
/** A date that replaces that day's weekly hours: closed, or open only at "hours". */
interface Holiday {
  date: string;
  name: string;
  hours?: [string, string][];
}

const DAYS: { id: DayKey; label: string }[] = [
  { id: 'mon', label: 'Monday' },
  { id: 'tue', label: 'Tuesday' },
  { id: 'wed', label: 'Wednesday' },
  { id: 'thu', label: 'Thursday' },
  { id: 'fri', label: 'Friday' },
  { id: 'sat', label: 'Saturday' },
  { id: 'sun', label: 'Sunday' },
];
const ZONES = ['Africa/Nairobi', 'Africa/Dar_es_Salaam', 'Africa/Kampala', 'Africa/Kigali', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Conakry', 'Europe/London', 'UTC'];

function summary(s: Schedule): string {
  const open = DAYS.filter((d) => s[d.id]?.length);
  if (!open.length) return 'Always open';
  const first = s[open[0].id]![0];
  const same = open.every((d) => s[d.id]!.length === 1 && s[d.id]![0][0] === first[0] && s[d.id]![0][1] === first[1]);
  const days = open.length === 5 && open.every((d) => ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d.id)) ? 'Mon to Fri' : open.map((d) => d.label.slice(0, 3)).join(', ');
  return same ? `${days}, ${first[0]} to ${first[1]}` : `${days}, varied hours`;
}

/** The next holiday or two, for the list. */
function upcoming(holidays: Holiday[] = []): string {
  const today = new Date().toISOString().slice(0, 10);
  const next = holidays.filter((h) => h.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  if (!next.length) return '';
  const label = (h: Holiday) => `${new Date(`${h.date}T12:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' })} ${h.name}`;
  return next.length > 2 ? `${label(next[0])}, ${label(next[1])} +${next.length - 2}` : next.map(label).join(', ');
}

export function BusinessHours() {
  const [rows, reload] = useLoad<HoursRow[]>('/business-hours');
  const [editing, setEditing] = useState<HoursRow | 'new' | null>(null);
  const done = () => {
    setEditing(null);
    reload();
  };
  return (
    <>
      <SectionHeader
        title="Business hours"
        lead="When queues take calls, including holidays. What callers hear when a queue is closed is set on the queue, under Queues."
        action={<AddButton onClick={() => setEditing('new')}>Add hours</AddButton>}
      />
      <Table head={['Name', 'Open', 'Holidays', 'Time zone', 'Used by']} empty={rows?.length === 0 && 'No hours yet, so every queue is always open.'}>
        {(rows ?? []).map((h) => (
          <Row key={h.id} onClick={() => setEditing(h)}>
            <Cell className="font-medium">{h.name}</Cell>
            <Cell>{summary(h.schedule)}</Cell>
            <Cell>{upcoming(h.holidays) || <span className="text-ink-3">None</span>}</Cell>
            <Cell>{h.timezone}</Cell>
            <Cell>{h.queues.length ? h.queues.join(', ') : <span className="text-ink-3">No queue yet</span>}</Cell>
          </Row>
        ))}
      </Table>
      {editing ? <HoursDrawer hours={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onDone={done} /> : null}
    </>
  );
}

function HoursDrawer({ hours, onClose, onDone }: { hours: HoursRow | null; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(hours?.name ?? '');
  const [timezone, setTimezone] = useState(hours?.timezone ?? 'Africa/Nairobi');
  const [days, setDays] = useState<Record<DayKey, { open: boolean; from: string; to: string }>>(() =>
    Object.fromEntries(
      DAYS.map((d) => {
        const r = hours?.schedule[d.id]?.[0];
        const weekday = !['sat', 'sun'].includes(d.id);
        return [d.id, r ? { open: true, from: r[0], to: r[1] } : { open: !hours && weekday, from: '08:00', to: '18:00' }];
      }),
    ) as Record<DayKey, { open: boolean; from: string; to: string }>,
  );
  const [holidays, setHolidays] = useState<{ date: string; name: string; closed: boolean; from: string; to: string }[]>(
    (hours?.holidays ?? [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((h) => ({ date: h.date, name: h.name, closed: !h.hours?.length, from: h.hours?.[0]?.[0] ?? '09:00', to: h.hours?.[0]?.[1] ?? '13:00' })),
  );
  const [busy, setBusy] = useState(false);
  const bad =
    DAYS.some((d) => days[d.id].open && days[d.id].from >= days[d.id].to) ||
    holidays.some((h) => !h.date || !h.name.trim() || (!h.closed && h.from >= h.to)) ||
    new Set(holidays.map((h) => h.date)).size !== holidays.length;

  async function save() {
    setBusy(true);
    const schedule = Object.fromEntries(DAYS.map((d) => [d.id, days[d.id].open ? [[days[d.id].from, days[d.id].to]] : []]));
    const body = {
      name: name.trim(),
      timezone,
      schedule,
      holidays: holidays.map((h) => ({ date: h.date, name: h.name.trim(), ...(h.closed ? {} : { hours: [[h.from, h.to]] }) })),
    };
    const ok = await act(() => (hours ? api('PATCH', `/business-hours/${hours.id}`, body) : api('POST', '/business-hours', body)), 'Hours saved');
    setBusy(false);
    if (ok) onDone();
  }

  return (
    <Drawer
      title={hours ? `Edit ${hours.name}` : 'Add business hours'}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={busy || bad || name.trim().length < 2}>
            {busy ? 'Saving…' : 'Save hours'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          {hours ? (
            <span className="ml-auto">
              <ConfirmButton
                confirmText="Delete hours"
                onConfirm={async () => {
                  const ok = await act(() => api('DELETE', `/business-hours/${hours.id}`).then(() => true), `${hours.name} deleted`);
                  if (ok) onDone();
                }}
              >
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <Field label="Name" hint="For example Weekdays or Ramadan hours.">
        {(id) => <input id={id} className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Time zone">
        {(id) => (
          <select id={id} className={inputClass} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {(ZONES.includes(timezone) ? ZONES : [timezone, ...ZONES]).map((z) => (
              <option key={z} value={z}>
                {z.replace('_', ' ')}
              </option>
            ))}
          </select>
        )}
      </Field>
      <p className="mb-2 text-sm font-medium">Open on</p>
      <ul className="flex flex-col gap-2">
        {DAYS.map((d) => {
          const v = days[d.id];
          const set = (patch: Partial<typeof v>) => setDays((x) => ({ ...x, [d.id]: { ...x[d.id], ...patch } }));
          return (
            <li key={d.id} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
              <label className="flex w-32 items-center gap-2.5 text-sm">
                <input type="checkbox" checked={v.open} onChange={(e) => set({ open: e.target.checked })} className="h-4 w-4 accent-[var(--color-ion)]" />
                {d.label}
              </label>
              {v.open ? (
                <span className="flex items-center gap-2 text-sm">
                  <input type="time" aria-label={`${d.label} opens`} value={v.from} onChange={(e) => set({ from: e.target.value })} className={`${inputClass} w-[112px] py-1.5`} />
                  to
                  <input type="time" aria-label={`${d.label} closes`} value={v.to} onChange={(e) => set({ to: e.target.value })} className={`${inputClass} w-[112px] py-1.5`} />
                </span>
              ) : (
                <span className="text-sm text-ink-3">Closed</span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mb-2 mt-6 flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium">Holidays and special days</p>
        <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => setHolidays([...holidays, { date: '', name: '', closed: true, from: '09:00', to: '13:00' }])}>
          + Add a date
        </Button>
      </div>
      <p className="mb-3 text-xs text-ink-3">A date listed here replaces that day's hours: closed all day, or open only at the hours you give.</p>
      <ul className="flex flex-col gap-2">
        {holidays.map((h, i) => {
          const set = (patch: Partial<typeof h>) => setHolidays(holidays.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          return (
            <li key={i} className="rounded-lg border border-line px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" aria-label={`Holiday ${i + 1} date`} value={h.date} onChange={(e) => set({ date: e.target.value })} className={`${inputClass} w-[150px] py-1.5`} />
                <input aria-label={`Holiday ${i + 1} name`} placeholder="Name, e.g. Madaraka Day" value={h.name} maxLength={60} onChange={(e) => set({ name: e.target.value })} className={`${inputClass} min-w-0 flex-1 py-1.5`} />
                <Button tone="ghost" className="px-2" aria-label={`Remove holiday ${i + 1}`} onClick={() => setHolidays(holidays.filter((_, j) => j !== i))}>
                  ×
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={h.closed} onChange={(e) => set({ closed: e.target.checked })} className="h-4 w-4 accent-[var(--color-ion)]" />
                  Closed all day
                </label>
                {!h.closed ? (
                  <span className="flex items-center gap-2">
                    open
                    <input type="time" aria-label={`Holiday ${i + 1} opens`} value={h.from} onChange={(e) => set({ from: e.target.value })} className={`${inputClass} w-[112px] py-1.5`} />
                    to
                    <input type="time" aria-label={`Holiday ${i + 1} closes`} value={h.to} onChange={(e) => set({ to: e.target.value })} className={`${inputClass} w-[112px] py-1.5`} />
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {bad ? <p className="mt-3 text-sm text-break">Check the times (opening before closing), and give each holiday a date and a name, each date once.</p> : null}
    </Drawer>
  );
}
